import type { Options } from '@zg-rentals/http-client';
import { getClientOptions, HTTPError } from '@zg-rentals/http-client';
import { getClient } from './client';
import { getGlobalMonitor } from '@zg-rentals/monitor-base';
import { type ApiRequestInterface, DatarouterError } from '@zg-rentals/web-base';

type ApiResponseSuccess<T> = {
  success: true;
  response: T;
};

type ApiResponseError = {
  success: false;
  error: {
    message: string;
    code?: string;
  };
  httpStatus?: number;
};

type ApiResponseDto<T> = ApiResponseSuccess<T> | ApiResponseError;

export type EnsureExtendsApiResponseDto<T extends ApiResponseDto<unknown>> = T;

async function withHttpErrorHandling<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    const text = await (err instanceof HTTPError
      ? err.response.text().catch(() => '<empty>')
      : (err?.message ?? 'unknown error'));

    if (err instanceof HTTPError) {
      throw new DatarouterError(text, '', err.response.status);
    } else {
      throw new DatarouterError(text, '', -1);
    }
  }
}

export async function httpApiRequest<G, P, R>(
  { getParams, postData, endpoint, method, isFormData, contextPath, unwrapApiLayerResponse }: ApiRequestInterface<G, P>,
  client = getClient(),
): Promise<R> {
  const clientOptions = getClientOptions(client);
  const monitor = clientOptions.monitoring === false ? undefined : getGlobalMonitor();

  // note: sanitize endpoint for monitoring
  const sanitizedEndpoint = endpoint.replace(/^\//, '').replace(/\//g, '_');
  monitor?.count(
    { name: `ts-client request volume total` },
    { name: `ts-client request volume endpoint ${sanitizedEndpoint}` },
  );

  const options: Options = { method };

  if (getParams) {
    if (getParams instanceof FormData) {
      options.body = getParams;
    } else {
      // Note: any is an escape hatch here just to help us filter out null/undefined values as
      // they are serialized as `null` or `undefined` directly in the query string, which is bad
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      options.searchParams = Object.entries(getParams).reduce<Record<string, any>>((acc, [key, value]) => {
        if (value != null) {
          acc[key] = value;
        }
        return acc;
      }, {});
    }
  }

  // note: http client set corrects request headers automatically
  if (postData) {
    if (isFormData) {
      // If formData, coerce body Object to FormData and include
      options.body = Object.entries(postData).reduce((formData, [key, value]) => {
        formData.append(key, value as string | Blob);
        return formData;
      }, new FormData());
    } else {
      // If not formData, just include as json body
      options.json = postData;
    }
  }

  // note: collapse multiple slashes into one
  let url = `${contextPath}/${endpoint}`.replace(/\/+/g, '/');

  const response = await withHttpErrorHandling(() =>
    client.extend(({ prefixUrl }) => {
      // Note: prefixUrl requires `url` not to start with `/`
      if (prefixUrl && url.startsWith('/')) {
        url = url.slice(1);
      }

      return { prefixUrl };
    })(url, options),
  ).catch((err) => {
    monitor?.count(
      { name: `ts-client response volume httpError total` },
      {
        name: `ts-client response volume httpError endpoint ${sanitizedEndpoint}`,
      },
      {
        name: `ts-client response volume httpError endpoint ${sanitizedEndpoint} httpStatus ${err instanceof HTTPError ? err.response.status : 'unknown'}`,
      },
    );

    throw err;
  });

  monitor?.count(
    { name: `ts-client response volume total` },
    {
      name: `ts-client response volume endpoint ${sanitizedEndpoint}`,
    },
    {
      name: `ts-client response volume endpoint ${sanitizedEndpoint} httpStatus ${response.status}`,
    },
  );

  const isJson = response.headers.get('content-type')?.includes('application/json');
  if (!isJson) {
    return response as R;
  }

  if (!unwrapApiLayerResponse) {
    return response.json<R>();
  }

  const apiResponse = await response.json<ApiResponseDto<R>>();

  if (apiResponse.success) {
    monitor?.count(
      { name: `ts-client response volume success total` },
      {
        name: `ts-client response volume success endpoint ${sanitizedEndpoint}`,
      },
      {
        name: `ts-client response volume success endpoint ${sanitizedEndpoint} httpStatus ${response.status}`,
      },
    );

    return apiResponse.response;
  } else {
    // note: casting required for apps not using strict mode
    const { error, httpStatus = response.status } = apiResponse as ApiResponseError;

    monitor?.count(
      { name: `ts-client response volume error total` },
      {
        name: `ts-client response volume error endpoint ${sanitizedEndpoint}`,
      },
      {
        name: `ts-client response volume error endpoint ${sanitizedEndpoint} httpStatus ${httpStatus}`,
      },
      {
        name: `ts-client response volume error endpoint ${sanitizedEndpoint} code ${error.code || 'unknown'}`,
      },
    );

    throw new DatarouterError(error.message, error.code || '', httpStatus, 'data' in error ? error.data : undefined);
  }
}
